import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AppInput,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getTotalEnergiesRiskControlFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import {
  getControlClass,
  getControlDocumentation,
  getControlEfficiency,
  getControlState,
  getControlType,
  getFrequency,
} from '../tools/totalEnergiesRiskControlTools';
import TotalEnergiesRiskRiskSelect from '../../../../components/form/Selects/TotalEnergiesRiskRiskSelect';
import ResponsibleSelect from '../../../../components/form/Selects/ResponsibleSelect';

export default function ControlFilterSidebarBlock() {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const filters = useSelector((state) => state.currentPage?.controlFilters);
  const [openOnce, setOpenOnce] = useState(true);
  const [noYesOptions] = useState([
    {
      label: <Translator file="user" name="general.no" />,
      value: 0,
    },
    {
      label: <Translator file="user" name="general.yes" />,
      value: 1,
    },
  ]);
  const [controlTypeOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlType(i),
    }))
  );
  const [controlClassOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlClass(i),
    }))
  );
  const [frequencyOptions] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: getFrequency(i),
    }))
  );
  const [controlDocumentationOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlDocumentation(i),
    }))
  );
  const [controlEfficiencyOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getControlEfficiency(i),
    }))
  );
  const [stateOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getControlState(i),
    }))
  );

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  const goToControls = (hexCode) =>
    navigate(
      path({
        name: 'user_module_totalEnergiesRisk_control_list',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      })
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_totalEnergiesRisk_risk_generateFilterHexCode'
        ),
        { moduleBridgeHex, filters: values }
      )
      .then(({ data }) => {
        setSubmitting(false);
        goToControls(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_totalEnergiesRisk_risk_getFilters'),
          {
            moduleBridgeHex,
            filtersHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            const f = getTotalEnergiesRiskControlFilterInitialValues();
            dispatch(
              currentPageAction.change(
                'controlFilters',
                { ...f, ...data },
                true
              )
            );
          }
        }),
    [moduleBridgeHex, filtersHex]
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'controlFilters',
          getTotalEnergiesRiskControlFilterInitialValues(),
          true
        )
      );
    }
  }, [filtersHex]);
  // < FILTERS

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="filtering"
                        />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() =>
                            navigate(
                              path({
                                name: 'user_module_totalEnergiesRisk_control_list',
                                params: {
                                  moduleBridgeHex,
                                  page: 1,
                                  filtersHex: 0,
                                },
                              })
                            )
                          }
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    <AppInput
                      title="Código"
                      icon={<AppRemixIcon icon="fingerprint" />}
                      value={values.code || ''}
                      onChange={(opt) => setFieldValue('code', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Nombre"
                      value={values.name || ''}
                      error={errors.name}
                      onChange={(opt) => setFieldValue('name', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Descripción"
                      value={values.description || ''}
                      error={errors.description}
                      onChange={(opt) => setFieldValue('description', opt)}
                      isClearable
                    />
                    <ResponsibleSelect
                      value={values.responsibleID}
                      error={errors?.responsibleID}
                      onHandleChange={(opt) =>
                        setFieldValue('responsibleID', opt)
                      }
                      apiCall="api_r_user_module_totalEnergiesRisk_control_getResponsibles"
                      hasSearchBox
                      isClearable
                    />
                    <TotalEnergiesRiskRiskSelect
                      title="Riesgos"
                      value={values?.riskIDs || []}
                      error={errors?.riskIDs}
                      onHandleChange={(opt) => setFieldValue('riskIDs', opt)}
                      hasSearchBox
                      isClearable
                      isMulti
                    />
                    <AppMultiSelect
                      options={stateOptions}
                      title="Estado de control"
                      value={values?.state || []}
                      onChange={(opt) => {
                        setFieldValue('state', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={controlTypeOptions}
                      title="Tipo de Control"
                      value={values?.controlType || []}
                      onChange={(opt) => {
                        setFieldValue('controlType', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={controlClassOptions}
                      title="Clase de Control"
                      value={values?.controlClass || []}
                      onChange={(opt) => {
                        setFieldValue('controlClass', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={frequencyOptions}
                      title="Frecuencia"
                      value={values?.frequency || []}
                      onChange={(opt) => {
                        setFieldValue('frequency', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={controlDocumentationOptions}
                      title="Documentación del control"
                      value={values?.controlDocumentation || []}
                      onChange={(opt) => {
                        setFieldValue('controlDocumentation', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    <AppMultiSelect
                      options={controlEfficiencyOptions}
                      title="Efectividad del control"
                      value={values?.controlEfficiency || []}
                      onChange={(opt) => {
                        setFieldValue('controlEfficiency', opt);
                      }}
                      allowSelectAll
                      hasSearchBox
                      hasSelectOptions
                    />
                    {permissionLevel === 0 && (
                      <AppSingleSelect
                        options={noYesOptions}
                        title="Mostrar desactivados"
                        value={values?.active}
                        error={errors?.active}
                        onChange={(opt) => {
                          setFieldValue('active', opt);
                        }}
                      />
                    )}
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}
